// @flow
import { filter, isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { NavigationUtil } from 'utils/NavigationUtil';
import { RouteNames } from 'router';
import { CmsPagesSelector } from 'modules/cmsPages/selector';
import { CmsPagesActions } from 'modules/cmsPages/actions';
import { HomeActions } from 'modules/home/actions';
import { HomeSelector } from 'modules/home/selector';
import { RestaurantActions } from 'modules/restaurant/actions';
import { RestaurantSelector } from 'modules/restaurant/selector';
import { TeamActions } from 'modules/team/action';
import { ProjectAnnouncementsSection } from 'components/templates/projectAnnouncementsSection';
import { TeamSelector } from 'modules/team/selector';
import { CareerActions } from 'modules/careers/actions';
import { CareerSelector } from 'modules/careers/selector';
import { ProjectSelector } from 'modules/project/selector';
import { PageLoader } from 'components/molecules/pageLoader';
import DiningBanner from 'components/organisms/diningBanner';
import { ContactUsSection } from 'components/templates/contactUsSection';
import CraftBeerSection from 'components/templates/craftBeerAndBrewery';
import ExploreRestaurantsNetwork from 'components/templates/exploreRestaurantsNetwork';
import FooterSection from 'components/templates/footer';
import PageTemplate from 'components/templates/pageTemplate';
import Team from 'components/templates/team';
import JoinOurTeamSection from 'components/templates/joinOurTeam';
import { useViewport } from 'components/templates/viewportProvider';
import { ProjectActions } from 'modules/project/actions';
import type { AppState } from 'modules/rootReducer';
import type {
  Contact,
  SiteSettings,
  Vertical,
  RecommendedScreenType,
} from 'types/common';
import { ShareModal } from 'components/templates/shareModal';
import { GetInTouchModal } from 'components/templates/getInTouchModal';
import RenderSEO from 'components/atoms/renderSEO';
import { FooterActions } from 'modules/footerLink/actions';
import { FooterSelector } from 'modules/footerLink/selector';

type Props = {
  isLoading: boolean,
  verticalId: number | null,
  verticals: Array<Vertical>,
  getVerticals: () => void,
  siteSettings: SiteSettings,
  getSiteSettings: () => void,
  restaurantBanner: any,
  getBannerData: () => void,
  craftBeerSectionData: any,
  getPageData: () => void,
  teamData: any,
  getTeamData: () => void,
  contact: Contact,
  getContactDetails: () => void,
  getAllRestaurants: () => void,
  restaurants: Array<RecommendedScreenType>,
};

const OurRestaurants = (props: Props) => {
  const {
    isLoading,
    getVerticals,
    verticals,
    verticalId,
    getSiteSettings,
    siteSettings,
    getBannerData,
    restaurantsBanner,
    getPageData,
    craftBeerSectionData,
    getTeamData,
    teamData,
    contact,
    getContactDetails,
    getAllRestaurants,
    footerLink,
    getFooterLinkData,
    restaurants,
    getAnnouncements,
    announcements,
    jobPosts,
    getJobPosts,
  } = props;
  const { isMobile } = useViewport();
  let [theme, setTheme] = useState(isMobile ? 'dark-theme' : 'light-theme');
  const [isGetInTouchModalOpen, setIsGetInTouchModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  useEffect(() => {
    getAnnouncements(verticalId);
    getContactDetails();
    getVerticals();
    getSiteSettings();
    getBannerData();
    getPageData();
    getFooterLinkData();
    getTeamData(verticalId, null);
    getAllRestaurants();
    getJobPosts(verticalId);
  }, []);

  if (isLoading) {
    return <PageLoader isMobile={isMobile} />;
  }

  if (
    !restaurantsBanner ||
    !craftBeerSectionData ||
    !teamData ||
    !contact ||
    !siteSettings ||
    !verticals ||
    !restaurants ||
    !jobPosts ||
    !footerLink
  ) {
    return null;
  }

  const selectedVertical = filter(verticals, ({ id }) => id === verticalId);
  const metaTagSEO =
    selectedVertical.length > 0 ? selectedVertical[0].metaTagSEO : '';

  if (restaurantsBanner.metaTitle)
    metaTagSEO.metaTitle = restaurantsBanner.metaTitle;
  if (restaurantsBanner.metaDescription)
    metaTagSEO.metaDescription = restaurantsBanner.metaDescription;
  if (restaurantsBanner.metaKeywords)
    metaTagSEO.metaKeywords = restaurantsBanner.metaKeywords;

  // Get in touch
  const onGetInTouchClick = () => {
    setIsGetInTouchModalOpen(true);
  };

  const closeGetInTouchModal = () => {
    setIsGetInTouchModalOpen(false);
  };

  //share
  const closeShareModal = () => {
    setIsShareModalOpen(false);
  };

  const CampaignCode = contact.campaignCode ? contact.campaignCode : '';

  const componentList = [
    ...(!isEmpty(restaurantsBanner)
      ? [
          {
            component: DiningBanner,
            data: restaurantsBanner,
            theme: isMobile ? 'dark-theme' : 'light-theme',
            sectionId: 'banner',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Restaurants',
                breadcrumbLink: RouteNames.restaurants,
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(restaurants)
      ? [
          {
            component: ExploreRestaurantsNetwork,
            sectionId: 'restaurants-network',
            theme: 'dark-theme',
            data: { data: restaurants, type: 'restaurant' },
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Restaurants',
                breadcrumbLink: RouteNames.restaurants,
              },
              {
                breadcrumbName: 'Restaurant Network',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
      ...(!isEmpty(announcements)
      ? [
          {
            component: ProjectAnnouncementsSection,
            data: { announcements },
            sectionId: 'announcements',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Restaurants',
                breadcrumbLink: RouteNames.restaurants,
              },
              {
                breadcrumbName: 'Announcements',
                breadcrumbLink: '#',
              },
            ],
            theme: 'dark-theme',
            sectionName: 'announcement-section',
          },
        ]
      : []),
    ...(!isEmpty(teamData)
      ? [
          {
            component: Team,
            sectionId: 'team',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Restaurants',
                breadcrumbLink: RouteNames.restaurants,
              },
              {
                breadcrumbName: 'The Team',
                breadcrumbLink: '#',
              },
            ],
            theme: 'dark-theme',
            data: teamData,
          },
        ]
      : []),
    ...(!isEmpty(craftBeerSectionData)
      ? [
          {
            component: CraftBeerSection,
            data: {
              craftBeerSectionData,
              showButton: false,
              showLineUpButton: false,
              noPadding: true,
            },
            theme: 'light-theme',
            sectionId: 'brewery',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Restaurants',
                breadcrumbLink: RouteNames.restaurants,
              },
              {
                breadcrumbName: 'Craft Beer & Brewery',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(jobPosts)
      ? [
          {
            component: JoinOurTeamSection,
            data: { jobPosts, onGetInTouchClick },
            sectionId: 'open-positions',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Restaurants',
                breadcrumbLink: RouteNames.restaurants,
              },
              {
                breadcrumbName: 'Join Our Team',
                breadcrumbLink: '#',
              },
            ],
            theme: 'dark-theme',
          },
        ]
      : []),
    {
      component: ContactUsSection,
      data: { contact, verticals, verticalId, type: 'no-padding' },
      location: props,
      sectionId: 'contact',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Restaurants',
          breadcrumbLink: RouteNames.restaurants,
        },
        {
          breadcrumbName: 'Contact Us',
          breadcrumbLink: '#',
        },
      ],
      theme: 'light-theme',
    },
    {
      component: FooterSection,
      theme: 'dark-theme',
      sectionId: 'te-links',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Restaurants',
          breadcrumbLink: RouteNames.restaurants,
        },
        {
          breadcrumbName: 'Links',
          breadcrumbLink: '#',
        },
      ],
      data: { siteSettings, footerLink },
    },
  ];

  return (
    <div>
      <RenderSEO metaTags={metaTagSEO} currentRouteName={'restaurants'} />
      <PageTemplate
        theme={theme}
        componentList={componentList}
        updateTheme={e => setTheme(e)}
        verticals={verticals}
        siteSettings={siteSettings}
        showGetInTouch={false}
        campaignCode={CampaignCode}
        salesEmail={contact.email}
      />
      <GetInTouchModal
        isOpen={isGetInTouchModalOpen}
        onClosePress={closeGetInTouchModal}
        campaignCode={CampaignCode}
        salesEmail={contact.email}
      />
      <ShareModal isOpen={isShareModalOpen} onClosePress={closeShareModal} />
    </div>
  );
};

const mapStateToProps = (state: AppState, props) => {
  const verticalKey = NavigationUtil.getVerticalKeyForLocation(props.location);

  return {
    isLoading:
      HomeSelector.isLoading(state) ||
      CmsPagesSelector.isCmsPageLoading(state) ||
      TeamSelector.getTeamLoading(state) ||
      FooterSelector.isLoading(state) ||
      CareerSelector.getJobPostsLoading(state) ||
      RestaurantSelector.getAllRestaurantsLoading(state) ||
      RestaurantSelector.getBeerLoading(state),
    verticals: HomeSelector.getVerticals(state),
    announcements: ProjectSelector.getAnnouncements(state),
    siteSettings: HomeSelector.getSiteSettings(state),
    restaurantsBanner: HomeSelector.getBanners(state, true, 'restaurants', ''),
    craftBeerSectionData: CmsPagesSelector.getCmsPages(
      state,
      'Craft Beer & Brewery',
      true
    ),
    teamData: TeamSelector.getTeamData(state),
    footerLink: FooterSelector.getFooterData(state),
    contact: HomeSelector.getContactForVertical(state, verticalKey),
    verticalId: HomeSelector.getVerticalIdForKey(verticalKey),
    restaurants: RestaurantSelector.getAllRestaurants(state),
    jobPosts: CareerSelector.getJobPosts(state),
  };
};

const mapDispatchToProps = dispatch => {
  const {
    getBannerData,
    getContactDetails,
    getVerticals,
    getSiteSettings,
  } = HomeActions;
  const { getPageData } = CmsPagesActions;
  const { getTeamData } = TeamActions;
  const { getAllRestaurants, getBeers } = RestaurantActions;
  const { getJobPosts } = CareerActions;
  const { getFooterLinkData } = FooterActions;
  const { getAnnouncements } = ProjectActions;
  return bindActionCreators(
    {
      getBannerData,
      getContactDetails,
      getSiteSettings,
      getVerticals,
      getTeamData,
      getPageData,
      getAllRestaurants,
      getJobPosts,
      getFooterLinkData,
      getBeers,
      getAnnouncements,
    },
    dispatch
  );
};
export default connect(mapStateToProps, mapDispatchToProps)(OurRestaurants);
